// ===========================================
// Revenue by Lesson Type Chart Component
// ===========================================
// Pie chart showing revenue distribution by lesson type

import { Card, CardContent, Typography, Box } from '@mui/material';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from 'recharts';

import { RevenueByLessonType } from '../../api/reports.api';
import { formatCurrency } from '../../hooks/useReports';

// ===========================================
// TYPES
// ===========================================

interface RevenueByTypeChartProps {
  data: RevenueByLessonType[];
  loading?: boolean;
}

// Brand colors for pie chart
const COLORS = ['#4580E4', '#FFCE00', '#96DAC9', '#FFAE9E', '#9B59B6'];

// ===========================================
// COMPONENT
// ===========================================

export default function RevenueByTypeChart({ data, loading = false }: RevenueByTypeChartProps) {
  if (loading || !data || data.length === 0) {
    return (
      <Card sx={{ height: '100%' }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Revenue by Lesson Type
          </Typography>
          <Box
            sx={{
              height: 300,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Typography variant="body2" color="text.secondary">
              {loading ? 'Loading...' : 'No data available'}
            </Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  // Transform data for Recharts
  const chartData = data.map((item) => ({
    name: item.lessonTypeName,
    value: item.totalRevenue / 100, // Convert cents to dollars for display
    invoiceCount: item.invoiceCount,
    percentage: item.percentage,
  }));

  // Custom label to show percentage
  const renderLabel = (entry: any) => {
    return `${entry.percentage.toFixed(1)}%`;
  };

  return (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Revenue by Lesson Type
        </Typography>
        <ResponsiveContainer width="100%" height={300}>
          <PieChart>
            <Pie
              data={chartData}
              cx="50%"
              cy="50%"
              labelLine={false}
              label={renderLabel}
              outerRadius={80}
              fill="#8884d8"
              dataKey="value"
            >
              {chartData.map((_, index) => (
                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
              ))}
            </Pie>
            <Tooltip
              formatter={(value: any) => (value !== undefined && value !== null) ? formatCurrency(Math.round(value * 100)) : 'N/A'}
              contentStyle={{
                backgroundColor: 'rgba(255, 255, 255, 0.95)',
                border: '1px solid #ccc',
                borderRadius: '4px',
              }}
            />
            <Legend
              verticalAlign="bottom"
              height={36}
              formatter={(value, entry: any) => (
                `${value} (${entry.payload.invoiceCount} invoices)`
              )}
            />
          </PieChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
}
